import React, { useEffect, useState } from "react";
import { getPackageConfig } from "components/ContentfulRenderer/ContentfulPackages/getPackageConfig";
import InsurancePackagesTable from "components/ContentfulRenderer/ContentfulPackages/InsurancePackagesTable/InsurancePackagesTable";
import MobileFullPackages from "components/ContentfulRenderer/ContentfulPackages/MobileFullPackages/MobileFullPackages";
import DateInput from "components/DateInput/DateInput";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import { BuyButton } from "components/Signup/components/BuyButton";
import ChooseDeductible from "components/Signup/components/ChooseDeductible";
import ChoosePaymentInterval from "components/Signup/components/ChoosePaymentInterval";
import InsurancePackages from "components/Signup/components/InsurancePackages";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import {
  getCurrentDraftForAnimal,
  getDraft,
  useAllValidAnimals,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import {
  useError,
  useIsLoading,
} from "components/Signup/store/selectors/metadata";
import {
  updateBillingInterval,
  updateInsuranceById,
} from "components/Signup/store/slices/animals";
import { addDays } from "date-fns";
import { useDispatch } from "react-redux";
import { useWebTranslation } from "translations";
import Gutter from "ui-kit/Gutter";
import { Container } from "ui-kit/Layout";
import ShowOnMedia from "ui-kit/ShowOnMedia";
import { Typography } from "ui-kit/Typography";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { getCopays } from "utils/site";
import { formatCurrency } from "utils/utils";
import { formatErrorMessage } from "utils/validation";
import { ProductEnum } from "../../../../../../__generated__/globalTypes";
import {
  amountToPay as amountToPayWithoutDiscount,
  getEarliestAllowedStartDate,
  validStartDate,
} from "../../../../../utils/insurance";
import { StepProps } from "../../types";
import DiscountCode from "components/Signup/components/DiscountCode";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import SignupDocuments from "components/Signup/components/SignupDocuments/SignupDocuments";
import { AnimalsTabs } from "components/Signup/components/AnimalsTabs";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import { useLeadAllianceTrackingQpTracking } from "components/Signup/analytics/useLeadAllianceQpTracking";
import { QPBanner } from "components/Signup/components/tractive/QPBanner";
import { QuotePreviewNotice } from "components/Signup/components/QuotePreviewNotice";
import { Modal } from "ui-kit";

const QuotePreviewView = ({ next, transitioning, isValid }: StepProps) => {
  const animals = useAllValidAnimals();

  const { t } = useWebTranslation();
  const animal = useCurrentAnimal();
  const isLoading = useIsLoading();
  const error = useError();

  const {
    drafts,
    limit,
    deductible,
    copay,
    billingInterval,
    startDate,
    discountCode,
    preventiveCareAddOnLimit,
    product,
    id,
  } = useCurrentInsuranceByAnimalId(animal.id);
  const dispatch = useDispatch();

  const [opacity, setOpacity] = useState(0);
  const [overlayDisplay, setOverlayDisplay] = useState(false);

  useLeadAllianceTrackingQpTracking({ orderId: id });

  useEffect(() => {
    setTimeout(() => {
      setOpacity(1);
    }, 200);
  }, []);

  const currentQuote = getCurrentDraftForAnimal(animal);

  const packageConfig = getPackageConfig({
    product: animal.species as unknown as ProductEnum,
  });

  if (!packageConfig) {
    return null;
  }

  packageConfig.benefits = packageConfig.benefits.filter(
    (benefit) => !["deductible"].includes(benefit.name)
  );

  const packagePrices = packageConfig.packages.map((pack) =>
    getAmountToPay({
      ...getDraft({
        drafts,
        limit: pack.limit,
        copay,
        preventiveCareAddOnLimit: preventiveCareAddOnLimit ?? 0,
        product,
      }),
      billingInterval,
    })
  );

  const selectedIndex = packageConfig.packages.findIndex(
    (pack) => limit === pack.limit
  );

  const amountToPay = getAmountToPay({
    ...getDraft({
      drafts,
      product,
      limit,
      copay,
      preventiveCareAddOnLimit: preventiveCareAddOnLimit ?? 0,
    }),
    billingInterval,
  });

  return (
    <div style={{ opacity, transition: "250ms ease-in" }}>
      {animals.length > 1 && <AnimalsTabs />}
      <InsurancePackages
        packageConfig={packageConfig}
        packagePrices={packagePrices}
        selectedIndex={selectedIndex}
        onChange={({ limit }) => {
          dispatch(
            updateInsuranceById({
              id,
              update: {
                limit,
                // Downgrade customers that have selected a higher preventive
                // budget if they select bronze
                ...((limit as number) === 1_000_00 &&
                (preventiveCareAddOnLimit ?? 0) > 100_00
                  ? { preventiveCareAddOnLimit: 100_00 }
                  : {}),
              },
            })
          );
        }}
        onCompare={() => setOverlayDisplay(true)}
        numberOfHighlightItems={0}
        isCopyExperiment
      />
      <Container>
        <Gutter size={6} />
        <Typography variant="h3" color="grey500">
          {t("choose_start_date.title")}
        </Typography>

        <Gutter size={3} />
        <Typography>{t("quote_preview.start_date")}</Typography>
        <Gutter size={3} />
        <DateInput
          ariaLabel={t("global.start_date")}
          value={startDate ?? getEarliestAllowedStartDate()}
          onChange={(startDate: { date: string }) => {
            dispatch(
              updateInsuranceById({ id, update: { startDate: startDate.date } })
            );
          }}
          min={
            new Date(
              validStartDate({
                birthday: animal.birthday ?? "2000-01-01",
                startDate,
              })
            )
          }
          max={addDays(new Date(), 364)}
        />

        <Gutter size={6} />
        <ChooseDeductible
          inverseCopayDisplay
          currentTotal={amountToPayWithoutDiscount({
            ...currentQuote,
            billingInterval,
          })}
          currentDeductible={deductible}
          currentCopay={copay}
          deductibles={[]}
          copays={getCopays().map((copay) => {
            return {
              value: copay,
              total: amountToPayWithoutDiscount({
                ...getDraft({
                  drafts,
                  limit,
                  copay,
                  product,
                  preventiveCareAddOnLimit: preventiveCareAddOnLimit ?? 0,
                }),
                billingInterval,
              }),
            };
          })}
          onChangeDeductibleCopay={(_, copay) => {
            dispatch(updateInsuranceById({ id, update: { copay } }));
          }}
        />

        {animals.length === 1 ? (
          <>
            <Gutter size={6} />
            <ChoosePaymentInterval
              value={billingInterval}
              onChange={(billingInterval) =>
                dispatch(updateBillingInterval(billingInterval))
              }
            />
          </>
        ) : null}

        <Gutter size={6} />
        <QuotePreviewNotice />

        <Gutter size={6} />
        <QPBanner species={animal.species} />

        <Gutter size={6} />
        <DiscountCode
          amountToPay={amountToPay}
          discount={{
            appliedCode: discountCode ?? "",
            onSubmit: (code: string) =>
              dispatch(
                updateInsuranceById({ id, update: { discountCode: code } })
              ),
            inProgress: isLoading,
          }}
          fees={currentQuote?.fees}
          billingInterval={billingInterval}
        />
        {error && (
          <>
            <Gutter size={10} />
            <ErrorMessage>
              <Typography color="error">{formatErrorMessage(error)}</Typography>
            </ErrorMessage>
          </>
        )}
      </Container>

      <Gutter size={4} />

      {animals.length > 1 ? (
        <QuotePreviewBottomBar next={next} />
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  amountToPay.old ? formatCurrency(amountToPay.old) : null
                }
                newAmount={formatCurrency(amountToPay.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={!isValid}
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}

      <SignupDocuments
        documents={[
          {
            title: t("insurance_documents.ipid"),
            url: "https://docs.lassie.co/fra/latest/ipid.pdf",
          },
          {
            title: t("insurance_documents.terms_and_conditions"),
            url: "https://docs.lassie.co/fra/latest/conditions-generales-de-lassurance-sante-animale-lassie.pdf",
          },
        ]}
      />
      <Modal
        isOpen={overlayDisplay}
        onClose={() => setOverlayDisplay(false)}
        title={t("quote_preview.compare_packages")}
      >
        <ShowOnMedia desktop={false} tablet={true} mobile={true}>
          <MobileFullPackages
            config={packageConfig}
            defaultSelectedIndex={selectedIndex}
            expanded
          />
        </ShowOnMedia>
        <ShowOnMedia desktop={true} tablet={false} mobile={false}>
          <InsurancePackagesTable config={packageConfig} />
        </ShowOnMedia>
      </Modal>
    </div>
  );
};

export default QuotePreviewView;
